import { useState } from "react";
import type { HoursSchedule, OverrideView, ProfileField } from "@starsach/shared-types";
import { useClearOverride, useSetOverride } from "../../api/hooks";
import { Dialog, ErrorState, RelativeTime, btnPrimary, btnSecondary, inputClass } from "../../components/ui";
import { AnnouncementRef } from "../customer/ProfileFieldEditors";

/**
 * Emergency close (requirements 8.9, the owner's ask of 2026-09-18): one
 * button, during an incident, by someone who is not a developer. It is
 * an override on the opening hours - an empty week plus the announcement
 * - so nothing authored changes, it ends on its expiry or by hand, and
 * the audit log records it like any other override. The confirmation
 * names the profile and the environment, because a live environment is
 * exactly where this is pressed in a hurry.
 *
 * After the first walk: the announcement is one of the profile's, chosen
 * by name (or words typed for this closure only); a close can start later
 * - the early finish before a long weekend, entered on the Thursday; and
 * the button itself says whether a close is on, scheduled, or neither.
 */
export type ClosureState = { kind: "none" } | { kind: "active" | "scheduled"; override: OverrideView };

/** An hours override with no open spans is a closure; one that has not started yet is a scheduled one. */
export function closureState(overrides: OverrideView[], hoursFields: Array<{ name: string }>, now: Date = new Date()): ClosureState {
  const closures = overrides.filter((o) => hoursFields.some((f) => o.field === `hours/${f.name}`) && isClosure(o.value) && (!o.expiresAt || Date.parse(o.expiresAt) > now.getTime()));
  const active = closures.find((o) => Date.parse(o.startsAt) <= now.getTime());
  if (active) return { kind: "active", override: active };
  const scheduled = closures.sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0];
  return scheduled ? { kind: "scheduled", override: scheduled } : { kind: "none" };
}

export function isClosure(value: unknown): boolean {
  return Boolean(value && typeof value === "object" && "weekly" in value && Array.isArray((value as { weekly: unknown }).weekly) && (value as { weekly: unknown[] }).weekly.length === 0);
}

export function EmergencyClose({
  environmentId,
  envName,
  profileId,
  profileName,
  hoursFields,
  announcements,
  state,
  onClose,
}: {
  environmentId: string;
  envName: string;
  profileId: string;
  profileName: string;
  hoursFields: Array<Extract<ProfileField, { type: "hours" }>>;
  announcements: string[];
  state: ClosureState;
  onClose: () => void;
}) {
  const set = useSetOverride(environmentId);
  const clear = useClearOverride(environmentId);
  const [fieldName, setFieldName] = useState(hoursFields[0]!.name);
  const field = hoursFields.find((f) => f.name === fieldName) ?? hoursFields[0]!;
  const [named, setNamed] = useState(field.value.closed_announcement && announcements.includes(field.value.closed_announcement) ? field.value.closed_announcement : (announcements[0] ?? ""));
  const [custom, setCustom] = useState(announcements.length === 0);
  const [words, setWords] = useState("We are closed at the moment. Please call back later.");
  const [reason, setReason] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const announcement = custom ? words : named;

  const value: HoursSchedule = { timezone: field.value.timezone, weekly: [], holidays: [], closed_announcement: announcement };
  const scheduled = Boolean(startsAt) && Date.parse(startsAt) > Date.now();

  if (state.kind !== "none" && !set.isSuccess) {
    const o = state.override;
    const said = (o.value as HoursSchedule).closed_announcement;
    return (
      <Dialog title={state.kind === "active" ? `${profileName} is closed in ${envName}` : `${profileName} will close in ${envName}`} onClose={onClose}>
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          {state.kind === "active" ? "Closed" : "Closes"} <RelativeTime iso={o.startsAt} />
          {o.expiresAt ? <> until <RelativeTime iso={o.expiresAt} /></> : ", until someone reopens it"} - {o.reason}, set by {o.setBy}.
          {said && <> Callers hear <q>{said}</q>.</>}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="button" className={btnPrimary} disabled={clear.isPending} onClick={() => clear.mutate({ profileId, field: o.field }, { onSuccess: onClose })}>
            {clear.isPending ? "Working…" : state.kind === "active" ? `Reopen ${profileName} now` : "Cancel the scheduled close"}
          </button>
          <button type="button" className={btnSecondary} onClick={onClose}>Close</button>
        </div>
        {clear.isError && <ErrorState error={clear.error} />}
      </Dialog>
    );
  }

  return (
    <Dialog title={`Close ${profileName} in ${envName}?`} onClose={onClose}>
      {set.isSuccess ? (
        <>
          <p role="status" className="text-sm">
            <strong>{profileName}</strong> {scheduled ? <>will close in <strong>{envName}</strong> at {new Date(startsAt).toLocaleString()}</> : <>is closed in <strong>{envName}</strong></>}. Callers hear <q>{announcement}</q>
            {expiresAt ? ` until ${new Date(expiresAt).toLocaleString()}` : " until someone reopens it"}.
          </p>
          <button type="button" className={`${btnPrimary} mt-4`} onClick={onClose}>Done</button>
        </>
      ) : (
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            set.mutate({
              profileId,
              field: `hours/${field.name}`,
              value,
              reason,
              ...(scheduled ? { startsAt: new Date(startsAt).toISOString() } : {}),
              ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
            });
          }}
        >
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            Every caller to this profile in <strong>{envName}</strong> hears the announcement and is treated as out of hours, from the time below (now, if empty) until the end time or until someone reopens it. Nothing you have authored changes.
          </p>
          {hoursFields.length > 1 && (
            <label className="block text-sm">
              Which hours
              <select className={inputClass} value={fieldName} onChange={(e) => setFieldName(e.target.value)}>
                {hoursFields.map((f) => <option key={f.name} value={f.name}>{f.name}</option>)}
              </select>
            </label>
          )}
          <div className="text-sm">
            <span className="block">Announcement callers hear</span>
            {!custom && <AnnouncementRef value={named} announcements={announcements} label="Closure announcement" onChange={setNamed} />}
            {custom && <textarea className={`${inputClass} min-h-20`} value={words} aria-label="Words for this closure" onChange={(e) => setWords(e.target.value)} required maxLength={4000} />}
            {announcements.length > 0 && (
              <label className="mt-1 flex items-center gap-1 text-xs text-neutral-600 dark:text-neutral-400">
                <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
                Say something else, just for this closure
              </label>
            )}
          </div>
          <label className="block text-sm">
            Why
            <input className={inputClass} value={reason} onChange={(e) => setReason(e.target.value)} required placeholder="Building evacuated" />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              From
              <input type="datetime-local" className={inputClass} value={startsAt} onChange={(e) => setStartsAt(e.target.value)} aria-describedby="close-from-hint" />
              <span id="close-from-hint" className="mt-1 block text-xs text-neutral-500">Leave empty to close now. A later time schedules the close.</span>
            </label>
            <label className="block text-sm">
              Until
              <input type="datetime-local" className={inputClass} value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} aria-describedby="close-until-hint" />
              <span id="close-until-hint" className="mt-1 block text-xs text-neutral-500">Leave empty to stay closed until someone reopens it.</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className={btnPrimary} disabled={set.isPending || !announcement.trim()}>
              {set.isPending ? "Working…" : scheduled ? `Schedule the close of ${profileName}` : `Close ${profileName} in ${envName} now`}
            </button>
            <button type="button" className={btnSecondary} onClick={onClose}>Cancel</button>
          </div>
          {set.isError && <ErrorState error={set.error} />}
        </form>
      )}
    </Dialog>
  );
}

/** An hours override with no open spans is a closure; anything else reads as the value it plays. */
export function describeOverride(value: unknown): string | undefined {
  if (value && typeof value === "object" && "weekly" in value) {
    const v = value as HoursSchedule;
    return v.weekly.length === 0 ? `Closed (emergency close)${v.closed_announcement ? ` - callers hear "${v.closed_announcement}"` : ""}` : "Different hours apply";
  }
  return undefined;
}
